'use client'

import { useState } from 'react'
import Link from 'next/link'

const sf = "var(--font-saira), 'Saira', sans-serif"
const MUTED = '#a1a1aa'
const GREEN = '#22c55e'

const COMPETITOR_FEES: Record<string, { maker: number; taker: number }> = {
  binance:    { maker: 0.10, taker: 0.10 },
  bybit_spot: { maker: 0.10, taker: 0.10 },
  okex:       { maker: 0.08, taker: 0.10 },
  kraken:     { maker: 0.16, taker: 0.26 },
  coinbase:   { maker: 0.40, taker: 0.60 },
  bitget:     { maker: 0.10, taker: 0.10 },
  bingx:      { maker: 0.10, taker: 0.10 },
  kucoin:     { maker: 0.10, taker: 0.10 },
  gateio:     { maker: 0.20, taker: 0.20 },
  mexc:       { maker: 0.00, taker: 0.05 },
}

const COMPETITOR_NAMES: Record<string, string> = {
  binance:    'Binance',
  bybit_spot: 'Bybit',
  okex:       'OKX',
  kraken:     'Kraken',
  coinbase:   'Coinbase',
  bitget:     'Bitget',
  bingx:      'BingX',
  kucoin:     'KuCoin',
  gateio:     'Gate.io',
  mexc:       'MEXC',
}

const TRADE_SIZES = [1_000, 5_000, 10_000, 50_000, 100_000]

interface Props {
  slug: string
  exchangeName: string
  makerFee: number
  takerFee: number
}

export default function FeeCostCalculator({ slug, exchangeName, makerFee, takerFee }: Props) {
  const [tradeSize, setTradeSize] = useState(10_000)

  // Build sorted rows
  const competitors = Object.entries(COMPETITOR_FEES)
    .filter(([id]) => id !== slug)
    .slice(0, 6)
    .map(([id, fees]) => ({
      id,
      name: COMPETITOR_NAMES[id] ?? id,
      maker: fees.maker,
      taker: fees.taker,
      isCurrent: false,
    }))

  const allRows = [
    { id: slug, name: exchangeName, maker: makerFee, taker: takerFee, isCurrent: true },
    ...competitors,
  ].sort((a, b) => a.taker - b.taker)

  const maxTakerCost = (Math.max(...allRows.map(r => r.taker)) / 100) * tradeSize
  const bestRow = allRows[0]
  const currentRow = allRows.find(r => r.isCurrent)!
  const currentCost = (currentRow.taker / 100) * tradeSize
  const bestCost = (bestRow.taker / 100) * tradeSize
  const savingsVsBest = currentCost - bestCost

  return (
    <div>
      {/* Header + trade size toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold font-[family-name:var(--font-saira)]">
          Fee cost per trade
        </h2>
        <div className="flex gap-1.5 flex-wrap">
          {TRADE_SIZES.map(s => (
            <button
              key={s}
              onClick={() => setTradeSize(s)}
              style={{
                fontFamily: sf,
                fontSize: 10,
                fontWeight: 700,
                padding: '4px 10px',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 1,
                background: tradeSize === s ? '#18181b' : 'transparent',
                border: tradeSize === s ? '1px solid #3f3f46' : '1px solid #27272a',
                color: tradeSize === s ? '#fff' : MUTED,
                transition: 'all 0.15s',
              }}
            >
              ${s >= 1000 ? `${s / 1000}k` : s}
            </button>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {allRows.map((row, i) => {
          const cost = (row.taker / 100) * tradeSize
          const extraCost = cost - bestCost
          const barPct = maxTakerCost > 0 ? (cost / maxTakerCost) * 100 : 0
          const isBest = i === 0

          return (
            <div
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                background: row.isCurrent ? 'rgba(34,197,94,0.04)' : 'transparent',
                border: row.isCurrent ? '1px solid rgba(34,197,94,0.15)' : '1px solid transparent',
                borderLeft: row.isCurrent ? '3px solid #22c55e' : '3px solid transparent',
              }}
            >
              {/* Name */}
              <div style={{ width: 76, flexShrink: 0, textAlign: 'right' }}>
                {row.isCurrent ? (
                  <span style={{ fontFamily: sf, fontSize: 12, fontWeight: 700, color: '#fff' }}>
                    {row.name}
                    <span style={{ display: 'block', fontSize: 8, color: GREEN, textTransform: 'uppercase', letterSpacing: 1 }}>you</span>
                  </span>
                ) : (
                  <Link
                    href={`/exchanges/${row.id}`}
                    style={{ fontFamily: sf, fontSize: 12, color: MUTED, textDecoration: 'none' }}
                    className="hover:text-white transition-colors"
                  >
                    {row.name}
                  </Link>
                )}
              </div>

              {/* Bar */}
              <div style={{ flex: 1, height: 22, background: '#0a0a0a', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, height: '100%',
                  width: `${Math.max(barPct, 2)}%`,
                  background: row.isCurrent
                    ? 'rgba(34,197,94,0.2)'
                    : isBest
                      ? 'rgba(74,222,128,0.12)'
                      : 'rgba(63,63,70,0.35)',
                  borderRight: row.isCurrent
                    ? '2px solid #22c55e'
                    : isBest
                      ? '2px solid #4ade80'
                      : 'none',
                  transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
                }} />
              </div>

              {/* Taker % */}
              <div style={{ width: 42, textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontFamily: sf, fontSize: 10, color: '#71717a' }}>
                  {row.taker === 0 ? 'Free' : `${row.taker.toFixed(2)}%`}
                </span>
              </div>

              {/* Cost */}
              <div style={{ width: 64, textAlign: 'right', flexShrink: 0 }}>
                <span style={{
                  fontFamily: sf, fontSize: 13, fontWeight: 700,
                  color: isBest ? GREEN : row.isCurrent ? '#fff' : '#d4d4d8',
                }}>
                  ${cost.toFixed(2)}
                </span>
              </div>

              {/* Extra vs best */}
              <div style={{ width: 68, textAlign: 'right', flexShrink: 0 }}>
                {extraCost > 0.001 ? (
                  <span style={{ fontFamily: sf, fontSize: 10, color: row.isCurrent ? '#f59e0b' : '#ef4444' }}>
                    +${extraCost.toFixed(2)}
                  </span>
                ) : (
                  <span style={{ fontFamily: sf, fontSize: 9, color: GREEN, textTransform: 'uppercase', letterSpacing: 1 }}>best</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer summary */}
      <div style={{
        marginTop: 12, paddingTop: 12, borderTop: '1px solid #18181b',
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontFamily: sf, fontSize: 10, color: MUTED }}>
          Taker fee cost on a{' '}
          <span style={{ color: '#fff', fontWeight: 700 }}>${tradeSize.toLocaleString()}</span> market order
        </span>

        {savingsVsBest > 0.01 ? (
          <span style={{ fontFamily: sf, fontSize: 10, color: '#f59e0b' }}>
            Switch to {bestRow.name} → save{' '}
            <strong style={{ color: '#fbbf24' }}>${savingsVsBest.toFixed(2)}</strong> per trade
          </span>
        ) : (
          <span style={{ fontFamily: sf, fontSize: 10, color: GREEN }}>
            {exchangeName} has the lowest fees in this comparison
          </span>
        )}
      </div>

      <p style={{ fontFamily: sf, fontSize: 10, color: '#3f3f46', marginTop: 8 }}>
        Standard spot taker fees. Actual fees may vary by VIP tier, token holdings, or promotions.
      </p>
    </div>
  )
}